/**
 * @fileoverview Product metrics card grid with period deltas. Volume facts
 * (units sold / refunded) live as pills next to the title in ProductHero;
 * per-platform ad spend and margin are intentionally absent (TODO: dedicated
 * surface for the Meta/Google split).
 * @module features/home/components/product-statistics/ProductMetricsPanel
 */

import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/lib/utils'
import { useCompactMoney } from '@/shared/hooks/useCompactMoney'
import type { StatisticsMetricsDTO } from '@/lib/statisticsService'
import type { PreviousPeriodTotals } from '@/features/home/hooks/useProductStatistics'
import { computeMetricDelta, type MetricDelta } from '@/features/home/utils/metricDeltas'

interface ProductMetricsPanelProps {
  metrics: StatisticsMetricsDTO
  /** Previous-window totals; omit to hide all deltas. */
  previous?: PreviousPeriodTotals | undefined
  className?: string
}

interface PrimaryMetric {
  key: string
  label: string
  value: string
  valueClass?: string
  delta: MetricDelta | null
}

export function ProductMetricsPanel({ metrics, previous, className }: ProductMetricsPanelProps): ReactElement {
  const { t } = useTranslation('features-home')
  const compactMoney = useCompactMoney()

  const sales = metrics.sales ?? 0
  const advertising = metrics.advertising ?? 0
  const cm2 = metrics.cm2 ?? 0
  const roas = metrics.roas ?? 0

  const primary: PrimaryMetric[] = [
    {
      key: 'revenue',
      label: t('productStatistics.kpi.revenue'),
      value: compactMoney(Math.round(sales)),
      delta: computeMetricDelta(sales, previous?.sales),
    },
    {
      key: 'profit',
      label: t('productStatistics.kpi.profit'),
      value: `${cm2 >= 0 ? '+' : ''}${compactMoney(Math.round(cm2))}`,
      valueClass: cm2 >= 0 ? 'text-positive' : 'text-negative',
      delta: computeMetricDelta(cm2, previous?.cm2, { absoluteWhenPrevNonPositive: true }),
    },
    {
      key: 'cogs',
      label: t('productStatistics.kpi.cogs'),
      value: compactMoney(Math.round(metrics.cogs ?? 0)),
      // No delta on purpose: COGS tracks units sold ~1:1, so a period delta
      // just echoes the revenue delta — and either sentiment coloring misreads
      // volume growth as cost news.
      delta: null,
    },
    {
      key: 'adSpend',
      label: t('productStatistics.kpi.adSpend'),
      value: compactMoney(Math.round(advertising)),
      // Neutral: spending more or less on ads is not inherently good or bad.
      delta: computeMetricDelta(advertising, previous?.advertising, { neutralSentiment: true }),
    },
    {
      key: 'roas',
      label: t('productStatistics.kpi.roas'),
      value: `${(Math.round(roas * 10) / 10).toFixed(1)}x`,
      delta: computeMetricDelta(roas, previous?.roas),
    },
  ]

  return (
    <div className={cn('grid grid-cols-2 lg:grid-cols-5 gap-3', className)}>
      {primary.map(({ key, label, value, valueClass, delta }) => (
        <div key={key} className="glass-card pl-6 pr-4 py-5 flex flex-col justify-center">
          <div className="mb-1.5 flex items-baseline gap-2">
            <div className="text-sm font-medium text-muted-foreground">{label}</div>
            {delta && <DeltaBadge delta={delta} />}
          </div>
          <div className={cn('text-2xl font-bold font-mono truncate', valueClass ?? 'text-foreground')}>{value}</div>
        </div>
      ))}
    </div>
  )
}

function DeltaBadge({ delta }: { delta: MetricDelta }): ReactElement {
  const compactMoney = useCompactMoney()
  const sentimentClass =
    delta.sentiment === 'neutral'
      ? 'text-muted-foreground/75'
      : delta.sentiment === 'positive'
        ? 'text-positive/75'
        : 'text-negative/75'

  if (delta.kind === 'absolute') {
    return (
      <span className={cn('text-xs font-medium whitespace-nowrap', sentimentClass)}>
        {delta.value >= 0 ? '+' : '-'}
        {compactMoney(Math.abs(delta.value))}
      </span>
    )
  }

  return (
    <span className={cn('text-xs font-medium whitespace-nowrap', sentimentClass)}>
      {delta.direction === 'up' ? '▲' : '▼'} {Math.abs(delta.value * 100).toFixed(0)}%
    </span>
  )
}
